import React, {
  Suspense,
  lazy,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import styled from "styled-components";
import { Alert } from "@interstate/components/Alert";
import { ErrorBoundary } from "@coxauto-ui/core/errors";
import { CenteredSpinner } from "@coxauto-ui/core/components";
import { loadCommunicationsRemote } from "../../utils/load-communications-remote";
import {
  MediaItem,
  SystemOfRecord
} from "@coxauto-ui/cxm/communications/interfaces";
import authClient from "@coxauto-ui/core-auth-get-auth-client-customer-conversation";
import { ConversationContext } from "../../context";
import { StyledModal } from "../components/styled-modal";
import { mediaLibraryModalTestId } from "../../testIds";

export type { MediaItem };

// MediaLibraryModule prop types are defined locally because the remote does not yet
// publish a shared types package. Keep these in sync with the integration contract:
//   INTEGRATION-CONTRACT (1).md (repo root) — attached to Feature F279586 in Rally.
// When the remote ships a types package, import from there and delete the local definitions.
//
// SupportedIdP: BRIDGE = Cox SSO Bridge; IDS = VinSolutions legacy identity provider
// authClient.getAuth is overridden by the host's IdP config (see overrideGetAuth),
// so the token type matches customerSystem: COMMON → BRIDGE, VIN → IDS.
type SupportedIdP = "BRIDGE" | "IDS";

function getIdpFromSystem(system: SystemOfRecord): SupportedIdP {
  return system === SystemOfRecord.VIN ? "IDS" : "BRIDGE";
}

// ProviderSource mirrors the remote's string union — this host only ever passes "CustomerInbox"
type ProviderSource = string;

interface MediaLibraryFeatures {
  upload?: boolean;
  delete?: boolean;
  select: boolean;
}

interface IdPConfig {
  supportedIdP: SupportedIdP;
  getJwtOverride: () => Promise<string>;
  errorCallback?: (error: string) => void;
}

interface MediaLibraryModuleProps {
  dealerId?: number;
  sourceApplication: ProviderSource;
  idPConfig: IdPConfig;
  features?: MediaLibraryFeatures;
  selectedMedia?: MediaItem[];
  onMediaSelect?: (media: MediaItem[]) => void;
  maxSelectionCount?: number;
  onCancelSelection?: () => void;
}

const MediaLibraryModule = lazy(
  () =>
    loadCommunicationsRemote(
      "media-library-remote",
      "./MediaLibraryModule"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ).then((m: any) => ({ default: m.MediaLibraryModule })) as Promise<{
      default: React.ComponentType<MediaLibraryModuleProps>;
    }>
);

const CUSTOMER_INBOX_FEATURES: MediaLibraryFeatures = { select: true };
const CUSTOMER_INBOX_SOURCE: ProviderSource = "CustomerInbox";
const MAX_MEDIA_SELECTION = 5;

const ModalBody = styled.div`
  position: relative;
  height: min(800px, 70vh);
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  border-radius: 8px;
`;

const ErrorFallback = styled.div`
  padding: 24px;
  color: #c00;
  text-align: center;
`;

export interface MediaLibraryModalProps {
  isOpen: boolean;
  isInserting?: boolean;
  onClose: () => void;
  onMediaSelect: (media: MediaItem[]) => void | Promise<void>;
  selectedMedia?: MediaItem[];
}

export function MediaLibraryModal({
  isOpen,
  isInserting = false,
  onClose,
  onMediaSelect,
  selectedMedia = []
}: MediaLibraryModalProps) {
  const { vinDealerId, customerSystem } = useContext(ConversationContext);
  const [mediaLibraryError, setMediaLibraryError] = useState<string | null>(
    null
  );

  const handleClose = useCallback(() => {
    setMediaLibraryError(null);
    onClose();
  }, [onClose]);

  const getJwtOverride = useCallback(() => authClient.getAuth(), []);

  const idPConfig = useMemo<IdPConfig>(
    () => ({
      supportedIdP: getIdpFromSystem(customerSystem),
      getJwtOverride,
      errorCallback: (error: string) => {
        // eslint-disable-next-line no-console
        console.error("[MediaLibraryModal] IdP auth error:", error);
      }
    }),
    [customerSystem, getJwtOverride]
  );

  const renderContent = () => {
    if (vinDealerId === 0) {
      return <CenteredSpinner id="media-library-loading-spinner" />;
    }

    return (
      <ErrorBoundary
        fallback={
          <ErrorFallback>
            Media library could not be loaded. Please try again later.
          </ErrorFallback>
        }
      >
        <Suspense
          fallback={<CenteredSpinner id="media-library-suspense-spinner" />}
        >
          <MediaLibraryModule
            dealerId={vinDealerId}
            features={CUSTOMER_INBOX_FEATURES}
            idPConfig={idPConfig}
            maxSelectionCount={MAX_MEDIA_SELECTION}
            sourceApplication={CUSTOMER_INBOX_SOURCE}
            onCancelSelection={handleClose}
            onMediaSelect={onMediaSelect}
          />
        </Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <StyledModal
      containerTestId={mediaLibraryModalTestId}
      isOpen={isOpen}
      title="Media"
      onClose={handleClose}
    >
      <ModalBody>
        {isInserting && (
          <LoadingOverlay>
            <CenteredSpinner id="media-library-insert-spinner" />
          </LoadingOverlay>
        )}
        {mediaLibraryError && (
          <Alert
            data-testid="media-library-error-alert"
            displayCloseButton={true}
            id="MediaLibraryErrorAlert"
            role="alert"
            type="caution"
            onCloseClick={() => setMediaLibraryError(null)}
          >
            <span>{mediaLibraryError}</span>
          </Alert>
        )}
        {renderContent()}
      </ModalBody>
    </StyledModal>
  );
}

export default MediaLibraryModal;
